import { useBundle } from '@amzn/react-arb-tools';
import { Button, ButtonVariant } from '@amzn/stencil-react-components/button';
import { Text } from '@amzn/stencil-react-components/text';
import { Range } from '@amzn/stencil-react-components/utils';
import { FormWrapper } from '@amzn/stencil-react-components/form';
import {
  Input,
  InputProps,
  InputWrapper,
} from '@amzn/stencil-react-components/input';
import {
  MessageBanner,
  MessageBannerType,
} from '@amzn/stencil-react-components/message-banner';
import {
  Spinner,
  SpinnerColorScheme,
  SpinnerSize,
} from '@amzn/stencil-react-components/spinner';
import {
  Col,
  GridContainer,
  GridItem,
} from '@amzn/stencil-react-components/layout';
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { HOMEPAGE_MESSAGE_BUNDLE_NAME } from '../constants';
import { t } from '../helpers';
import { useCreateInterest } from 'src/hooks/useCreateInterest';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface FieldConfig {
  name: string;
  text: string;
  placeholder: string;
  required: boolean;
  errorText: string;
  type: InputProps['type'];
  footerGridSize: Range<1, 13>;
  cardGridSize: Range<1, 13>;
}

const signUpFieldsConfig: FieldConfig[] = [
  {
    name: 'firstName',
    text: 'first_name_field',
    placeholder: 'first_name_field_placeholder',
    required: true,
    errorText: 'field_is_required',
    type: 'text',
    footerGridSize: 2,
    cardGridSize: 6,
  },
  {
    name: 'lastName',
    text: 'last_name_field',
    placeholder: 'last_name_field_placeholder',
    required: true,
    errorText: 'field_is_required',
    type: 'text',
    footerGridSize: 2,
    cardGridSize: 6,
  },
  {
    name: 'email',
    text: 'email_field',
    placeholder: 'email_field_placeholder',
    required: true,
    errorText: 'email_field_invalid',
    type: 'email',
    footerGridSize: 3,
    cardGridSize: 12,
  },
  {
    name: 'referral',
    text: 'referral_field',
    placeholder: 'referral_field_placeholder',
    required: false,
    errorText: 'field_is_required',
    type: 'text',
    footerGridSize: 3,
    cardGridSize: 12,
  },
];

const FOOTER_SUBMIT_GRID_SIZE = 2;
const CARD_SUBMIT_GRID_SIZE = 12;

export interface SignUpSectionProps {
  footer: boolean;
  onSuccess?: () => void;
}

const SignUpSection = (props: SignUpSectionProps) => {
  const [bundle] = useBundle(HOMEPAGE_MESSAGE_BUNDLE_NAME);
  const { mutateAsync: createInterestMutation } =
    useCreateInterest('signup_section');

  // Handles multiple sign up sections
  const instanceId = React.useId();
  const fieldId = (config: FieldConfig): string =>
    `${instanceId}${config.name}-id`;

  const [signUpFields, setSignUpFields] = React.useState<Map<string, string>>(
    () => new Map(signUpFieldsConfig.map((config) => [config.name, ''])),
  );
  const [fieldTouched, setFieldTouched] = React.useState<Map<string, boolean>>(
    new Map(),
  );
  const [submitClicked, setSubmitClicked] = React.useState<number>(0);
  const [submitFailed, setSubmitFailed] = React.useState<boolean>(false);
  const [submitLoading, setSubmitLoading] = React.useState<boolean>(false);
  const submitLoadingRef = React.useRef<boolean>(false);

  const [searchParams] = useSearchParams();
  const partner = searchParams.get('partner');

  const isFieldValid = (config: FieldConfig): boolean => {
    const value = signUpFields.get(config.name)!;

    return (
      !config.required ||
      (config.type === 'email' ? EMAIL_PATTERN.test(value) : value !== '')
    );
  };

  const fieldHasError = (config: FieldConfig): boolean =>
    !!fieldTouched.get(config.name) && !isFieldValid(config);

  const validationErrors = signUpFieldsConfig.filter(
    (config) => !isFieldValid(config),
  );
  const showValidationErrors = submitClicked > 0 && validationErrors.length > 0;

  React.useEffect(() => {
    if (submitClicked > 0 && validationErrors.length > 0) {
      document.getElementById(fieldId(validationErrors[0]))?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitClicked]);

  const updateAnswers = (fieldName: string, value: string): void => {
    const updatedFields = new Map(signUpFields);
    updatedFields.set(fieldName, value);
    setSignUpFields(updatedFields);

    updatedFieldsTouched(fieldName);
  };

  const updatedFieldsTouched = (fieldName: string): void => {
    const updatedFieldsTouched = new Map(fieldTouched);
    updatedFieldsTouched.set(fieldName, true);
    setFieldTouched(updatedFieldsTouched);
  };

  const submitInterest = async (): Promise<void> => {
    setSubmitClicked((prev) => prev + 1);
    setSubmitFailed(false);

    const updatedFieldsTouched = new Map(fieldTouched);
    signUpFieldsConfig.forEach((config) =>
      updatedFieldsTouched.set(config.name, true),
    );
    setFieldTouched(updatedFieldsTouched);

    if (validationErrors.length > 0 || submitLoadingRef.current) {
      return;
    }
    setSubmitLoading(true);
    submitLoadingRef.current = true;

    await createInterestMutation({
      firstName: signUpFields.get('firstName')!,
      lastName: signUpFields.get('lastName')!,
      partner: partner,
      email: signUpFields.get('email')!,
      referral: signUpFields.get('referral')!,
    })
      .then(() => {
        props.onSuccess?.();
      })
      .catch(() => {
        setSubmitFailed(true);
      })
      .finally(() => {
        setSubmitLoading(false);
        submitLoadingRef.current = false;
      });
  };

  return (
    <Col width="100%" gridGap="dimensions.spacing.300">
      {(showValidationErrors || submitFailed) && (
        <MessageBanner
          type={MessageBannerType.Error}
          aria-live="assertive"
          isDismissible
          dataTestId="sign-up-error-banner-test-id"
          onDismissed={() => {
            setSubmitClicked(0);
            setSubmitFailed(false);
          }}
        >
          {submitFailed ? (
            <Text>{t(bundle, 'submit_interest_failure')}</Text>
          ) : (
            <Col gridGap="dimensions.spacing.100" width="100%">
              <Text>{t(bundle, 'submit_interest_error_header')}</Text>
              <Col gridGap="dimensions.spacing.100" width="100%" role="list">
                {validationErrors.map((config) => (
                  <Text role="listitem" key={config.name}>
                    {`${t(bundle, config.text) || config.text} ${t(
                      bundle,
                      config.errorText,
                    )}`}
                  </Text>
                ))}
              </Col>
            </Col>
          )}
        </MessageBanner>
      )}
      <GridContainer dataTestId="sign-up-test-id">
        {signUpFieldsConfig.map((field) => (
          <GridItem
            key={field.name}
            lg={props.footer ? field.footerGridSize : field.cardGridSize}
            md={8}
          >
            <FormWrapper width={'100%'}>
              <InputWrapper
                id={fieldId(field)}
                labelText={t(bundle, field.text) || field.text}
                required={field.required}
                error={fieldHasError(field)}
                footer={
                  fieldHasError(field) ? t(bundle, field.errorText) : undefined
                }
              >
                {(inputProps) => (
                  <Input
                    {...inputProps}
                    dataTestId={`sign-up-input-${field.name}-test-id`}
                    name={field.name}
                    type={field.type}
                    value={signUpFields.get(field.name)}
                    onChange={(event) => {
                      updateAnswers(field.name, event.target.value);
                    }}
                    placeholder={t(bundle, field.placeholder)}
                    onBlur={() => updatedFieldsTouched(field.name)}
                  />
                )}
              </InputWrapper>
            </FormWrapper>
          </GridItem>
        ))}
        <GridItem
          lg={props.footer ? FOOTER_SUBMIT_GRID_SIZE : CARD_SUBMIT_GRID_SIZE}
          md={8}
        >
          <Col width={'100%'}>
            <Button
              variant={ButtonVariant.Primary}
              dataTestId="submit-button-sign-up-test-id"
              onClick={() => submitInterest()}
            >
              {submitLoading && (
                <Spinner
                  loadingText={t(bundle, 'submit_interest_loading')}
                  size={SpinnerSize.Small}
                  style={{ marginRight: '8px' }}
                  colorScheme={SpinnerColorScheme.Inverted}
                />
              )}
              <Text textAlign="center">
                {t(bundle, 'submit_interest_button')}
              </Text>
            </Button>
          </Col>
        </GridItem>
      </GridContainer>
    </Col>
  );
};

export default SignUpSection;
